import React, { PropsWithChildren, useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { When } from 'react-if';
import { QuestionmarkSmallCircleColorIcon } from '@zep/icons';
import { SpaceUsageType } from '@zep/types';
import { Input, InputTextLengthLabel, Stack, TextField, Toggle, Tooltip } from '@zep/ui';
import { cn, isMobileDevice } from '@zep/utils';
import { CreateSpaceSchemaType, SpaceSchemaSpec } from '../schema';
interface SpaceSettingsFormProps {
  disabled?: boolean;
  className?: string;
}
export const SpaceSettingsForm = ({
  disabled = false,
  className
}: SpaceSettingsFormProps) => {
  const {
    t
  } = useTranslation(['common']);
  const {
    register,
    watch,
    setValue,
    getValues
  } = useFormContext<CreateSpaceSchemaType>();
  const isSetPassword = watch('enablePassword');
  const spaceUsageType = watch('spaceUsageType');
  const name = watch('name');
  const enableRtc = watch('enableRtc');
  const toggleSetPassword = () => {
    setValue('enablePassword', !getValues('enablePassword'));
  };
  const toggleSpaceUsageType = (event: React.FormEvent<HTMLInputElement>) => {
    setValue('spaceUsageType', event.currentTarget.checked ? SpaceUsageType.PUBLIC : SpaceUsageType.PRIVATE);
  };
  const toggleRtc = (event: React.FormEvent<HTMLInputElement>) => {
    setValue('enableRtc', event.currentTarget.checked);
  };
  useEffect(() => {
    if (!isSetPassword) {
      setValue('password', '');
    }
  }, [setValue, isSetPassword]);
  return <Stack gap={24} className={className} data-sentry-element="Stack" data-sentry-component="SpaceSettingsForm" data-sentry-source-file="SpaceSettingsForm.tsx">
      <TextField label={<TextFiledLabel>
            <span className="text-subtitle font-semibold text-gray-900">
              {t('common:createSpaceModal.nameLabel')}
            </span>
          </TextFiledLabel>} rightSlot={<InputTextLengthLabel inputLength={name.length} maxInputLength={SpaceSchemaSpec.name.maxLength} />} data-sentry-element="TextField" data-sentry-source-file="SpaceSettingsForm.tsx">
        <Input size={'md'} isFullWidth style={{
        fontSize: '16px'
      }} {...register('name')} placeholder={t('common:createSpaceModal.namePlaceHolder')} maxLength={SpaceSchemaSpec.name.maxLength} disabled={disabled} data-sentry-element="Input" data-sentry-source-file="SpaceSettingsForm.tsx" />
      </TextField>
      <Toggle isFullWidth checked={spaceUsageType === SpaceUsageType.PUBLIC} onChange={toggleSpaceUsageType} label={<Stack direction={'row'} gap={4} style={{
      alignItems: 'center'
    }}>
            <span className="text-subtitle font-semibold text-gray-900">
              {t('common:spaces.create.spaceUsageType')}
            </span>
            <div className={cn('rounded-full px-[8px] ml-auto py-[2px]', {
        'bg-primaryBg text-primary': spaceUsageType === SpaceUsageType.PUBLIC,
        'bg-gray-200 text-gray-600': spaceUsageType === SpaceUsageType.PRIVATE
      })}>
              <span className="text-caption-1 font-[600]">
                {spaceUsageType === SpaceUsageType.PUBLIC ? t('common:spaces.create.public') : t('common:spaces.create.private')}
              </span>
            </div>
            <When condition={!isMobileDevice()}>
              <Tooltip placement="top" tooltipClassName="max-w-[300px] break-words" title={t('common:spaces.create.tooltip')}>
                <QuestionmarkSmallCircleColorIcon width={24} height={24} />
              </Tooltip>
            </When>
          </Stack>} labelPlacement={'right'} disabled={disabled} data-sentry-element="Toggle" data-sentry-source-file="SpaceSettingsForm.tsx" />
      <Toggle isFullWidth onChange={toggleRtc} checked={enableRtc} label={<Stack direction={'row'} gap={4} style={{
      alignItems: 'center'
    }}>
            <span className="text-subtitle font-semibold text-gray-900">
              {t('common:spaces.create.ad55123c')}
            </span>
            <When condition={!isMobileDevice()}>
              <Tooltip placement="top" tooltipClassName="max-w-[300px] break-words" title={t('common:spaces.create.076f8fa6')}>
                <QuestionmarkSmallCircleColorIcon width={24} height={24} />
              </Tooltip>
            </When>
          </Stack>} labelPlacement={'right'} disabled={disabled} data-sentry-element="Toggle" data-sentry-source-file="SpaceSettingsForm.tsx" />
      <Stack gap={5} data-sentry-element="Stack" data-sentry-source-file="SpaceSettingsForm.tsx">
        <Toggle label={<span className="text-subtitle font-semibold text-gray-900">
              {t('common:createSpaceModal.passwordLabel')}
            </span>} labelPlacement={'right'} isFullWidth checked={isSetPassword} onChange={toggleSetPassword} disabled={disabled} data-sentry-element="Toggle" data-sentry-source-file="SpaceSettingsForm.tsx" />
        <When condition={isSetPassword} data-sentry-element="When" data-sentry-source-file="SpaceSettingsForm.tsx">
          <Input size={'md'} style={{
          fontSize: '16px'
        }} {...register('password')} placeholder={t('common:createSpaceModal.passwordPlaceHolder')} maxLength={SpaceSchemaSpec.password.maxLength} disabled={disabled} data-sentry-element="Input" data-sentry-source-file="SpaceSettingsForm.tsx" />
        </When>
      </Stack>
    </Stack>;
};
const TextFiledLabel = (prop: PropsWithChildren) => <span className="text-subtitle font-semibold" data-sentry-component="TextFiledLabel" data-sentry-source-file="SpaceSettingsForm.tsx">{prop.children}</span>;